import React, { useCallback, useEffect, useState } from "react";
import api, { API_BASE } from "../lib/api";
import StatusBadge from "./StatusBadge";
import UploadDialog from "./UploadDialog";
import EditDocumentDialog from "./EditDocumentDialog";
import PreviewDialog from "./PreviewDialog";
import { fmtDate } from "../lib/utils-app";
import { toast } from "sonner";
import { Upload, Download, Trash2, FileText, Pencil, Eye } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { canUpload, canDownload } from "../lib/utils-app";

export default function DocumentsList({ entityType, entityId, companyId, categories, onChanged }) {
  const { user } = useAuth();
  const up = canUpload(user), dl = canDownload(user);
  const [docs, setDocs] = useState([]);
  const [openUpload, setOpenUpload] = useState(false);
  const [edit, setEdit] = useState(null);
  const [preview, setPreview] = useState(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/documents", { params: { entity_type: entityType, entity_id: entityId } });
    setDocs(data);
    onChanged && onChanged();
  }, [entityType, entityId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = d.original_filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Errore download"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare il documento?")) return;
    await api.delete(`/documents/${id}`);
    toast.success("Documento eliminato");
    load();
  };

  const filtered = docs.filter(d =>
    !filter || d.nome?.toLowerCase().includes(filter.toLowerCase()) || d.categoria?.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <input placeholder="Filtra documenti…" value={filter} onChange={(e) => setFilter(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm w-full sm:w-64" />
        {up && <button onClick={() => setOpenUpload(true)} data-testid="upload-doc-button"
          className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg text-sm font-medium">
          <Upload size={16} /> Carica documento
        </button>}
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-lg py-10 text-center text-slate-500">
          <FileText size={28} className="mx-auto mb-2 text-slate-400" />
          Nessun documento caricato.
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Categoria</th>
                <th className="text-left px-4 py-3 font-medium">Nome / File</th>
                <th className="text-left px-4 py-3 font-medium">Emissione</th>
                <th className="text-left px-4 py-3 font-medium">Scadenza</th>
                <th className="text-left px-4 py-3 font-medium">Stato</th>
                <th className="text-right px-4 py-3 font-medium">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(d => (
                <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">{d.categoria}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{d.nome}</div>
                    <div className="text-xs text-slate-500 font-mono">{d.original_filename}</div>
                  </td>
                  <td className="px-4 py-3">{fmtDate(d.data_emissione)}</td>
                  <td className="px-4 py-3">{fmtDate(d.data_scadenza)}</td>
                  <td className="px-4 py-3"><StatusBadge status={d.status} /></td>
                  <td className="px-4 py-3 text-right">
                    {dl && <button onClick={() => setPreview(d)} data-testid={`preview-doc-${d.id}`} className="text-slate-700 hover:text-slate-900 p-1.5" title="Anteprima"><Eye size={16} /></button>}
                    {dl && <button onClick={() => download(d)} className="text-blue-700 hover:text-blue-900 p-1.5" title="Scarica"><Download size={16} /></button>}
                    {up && <button onClick={() => setEdit(d)} data-testid={`edit-doc-${d.id}`} className="text-slate-700 hover:text-slate-900 p-1.5" title="Modifica"><Pencil size={16} /></button>}
                    {up && <button onClick={() => remove(d.id)} className="text-red-600 hover:text-red-800 p-1.5" title="Elimina"><Trash2 size={16} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <UploadDialog open={openUpload} onClose={() => setOpenUpload(false)}
        entityType={entityType} entityId={entityId} companyId={companyId}
        categories={categories} onUploaded={load} />
      {edit && <EditDocumentDialog doc={edit} onClose={() => setEdit(null)} onSaved={load} />}
      {preview && <PreviewDialog doc={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
